import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import TradeOrderVM from './TradeOrderVM';
import { observer } from 'mobx-react';
import Loading from '../../../App/Components/Loading/Loading';
import { TextConstants } from '../../../Common/TextConstants';
import TradeOrderLookup from '../Models/Lookups/TradeOrderLookup';
import UpdateTradeOrderCommand from '../Models/Commands/UpdateTradeOrderCommand';
import { EnumHelper } from '@singularsystems/neo-core';
import { TradeOrderStatus } from '../Models/Enums/TradeOrderStatus';

@observer
export default class TradeOrderView extends Views.ViewBase<TradeOrderVM> {

    // Class property refs for file inputs
    private confirmFileInputRef: HTMLInputElement | null = null;
    private rejectFileInputRef: HTMLInputElement | null = null;

    private renderFileUpload(id: string, type: 'confirm' | 'reject') {
        const inputRef = type === 'confirm' 
            ? (el: HTMLInputElement | null) => { this.confirmFileInputRef = el; } 
            : (el: HTMLInputElement | null) => { this.rejectFileInputRef = el; };
        const getInputRef = () => type === 'confirm' ? this.confirmFileInputRef : this.rejectFileInputRef;
        
        const hasFile = this.viewModel.uploadedFile && this.viewModel.uploadFileType === type;

        return (
            <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block', color: '#333' }}>
                    Attachment (Optional)
                </label>
                <div
                    style={{
                        border: hasFile ? '1.5px solid #4caf50' : '1.5px dashed #bdbdbd',
                        borderRadius: 6,
                        padding: 20,
                        textAlign: 'center',
                        background: hasFile ? '#f1f8f4' : '#fafbfc',
                        position: 'relative',
                        minHeight: 100,
                        transition: 'all 0.2s ease'
                    }}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={e => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        this.viewModel.handleFileUpload(file ?? null, type);
                        const ref = getInputRef();
                        if (ref) ref.value = '';
                    }}
                >
                    {!hasFile ? (
                        <>
                            <div style={{ fontSize: 32, color: '#bdbdbd', marginBottom: 8 }}>
                                <i className="fa fa-upload" />
                            </div>
                            <div style={{ fontWeight: 500, color: '#666', marginBottom: 4 }}>
                                Drag and drop file here
                            </div>
                            <div style={{ color: '#888', marginBottom: 12, fontSize: 13 }}>
                                or
                            </div>
                            <label 
                                htmlFor={id} 
                                style={{ 
                                    cursor: 'pointer', 
                                    color: '#1976d2', 
                                    fontWeight: 500, 
                                    border: '1px solid #1976d2', 
                                    borderRadius: 4, 
                                    padding: '6px 20px', 
                                    display: 'inline-block',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#e3f2fd')}
                                onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                                Browse files
                                <input
                                    id={id}
                                    ref={inputRef}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                    style={{ display: 'none' }}
                                    onChange={e => {
                                        const file = e.target.files && e.target.files[0];
                                        this.viewModel.handleFileUpload(file ?? null, type);
                                        const ref = getInputRef();
                                        if (ref) ref.value = '';
                                    }}
                                />
                            </label>
                            <div style={{ color: '#999', fontSize: 12, marginTop: 12 }}>
                                Max 10MB • PDF, Images, Word, Excel
                            </div>
                        </>
                    ) : (
                        <div style={{ padding: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                                <i className="fa fa-check-circle" style={{ color: '#4caf50', fontSize: 24, marginRight: 8 }} />
                                <span style={{ fontWeight: 600, color: '#2e7d32' }}>File Selected</span>
                            </div>
                            <div style={{ color: '#555', marginBottom: 4 }}>
                                <strong>{this.viewModel.uploadedFile!.name}</strong>
                            </div>
                            <div style={{ color: '#777', fontSize: 13, marginBottom: 12 }}>
                                {(this.viewModel.uploadedFile!.size / 1024).toFixed(2)} KB
                            </div>
                            <Neo.Button
                                variant="danger"
                                size="sm"
                                isOutline
                                icon="fa fa-times"
                                onClick={() => {
                                    this.viewModel.clearUploadedFile();
                                    const ref = getInputRef();
                                    if (ref) ref.value = '';
                                }}
                            >
                                Remove File
                            </Neo.Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    constructor(props: unknown) {
        super("Trade Orders", TradeOrderVM, props);
    }

    public async componentDidMount() {
        // Check permission before loading the page
        if (!this.viewModel.canViewAll) {
            alert("You do not have permission to view trade orders.");
            this.viewModel.navigateToHome()
            return;
        }
    }

    public render() {
        // Don't render anything if user doesn't have permission
        if (!this.viewModel.canViewAll) {
            return (
                <Neo.Card title="Access Denied" icon="fa fa-exclamation-triangle">
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <p>You do not have permission to view trade orders.</p>
                        <Neo.Button
                            variant="primary"
                            icon="fa fa-home"
                            onClick={() => this.viewModel.navigateToHome()}
                        >
                            Return to Home
                        </Neo.Button>
                    </div>
                </Neo.Card>
            );
        }

        const disableInlineActions = this.viewModel.selectedTradeOrderIDs.length > 0;

        return (
            <>
                {this.viewModel.taskRunner.isBusy && <Loading />}
                {/* Search Criteria Card */}
                <Neo.Card title="Search Criteria" icon="fa fa-search">
                    <Neo.Form model={this.viewModel.tradeOrderSearchCriteria} onSubmit={() => this.viewModel.pageManager.refreshData()}>
                        <Neo.GridLayout md={4}>
                            <Neo.FormGroupInline
                                bind={this.viewModel.tradeOrderSearchCriteria.meta.staticServiceProviderFileGroupingID}
                                select={{
                                    displayMember: "serviceProviderGroupingName",
                                    items: this.viewModel.allServiceProviders,
                                    allowNulls: true,
                                    nullText: "All Brokers",
                                    onItemSelected: () => this.viewModel.pageManager.refreshData()
                                }}
                                placeholder="Service Provider"
                            />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.accountNumber} />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.referenceNumber} />
                            <Neo.FormGroupInline
                                bind={this.viewModel.tradeOrderSearchCriteria.meta.tradeOrderStatusId}
                                select={{
                                    items: EnumHelper.asList(TradeOrderStatus),
                                    displayMember: "name",
                                    valueMember: "id",
                                    allowNulls: true,
                                    nullText: "All Statuses"
                                }}
                                onChange={() => this.viewModel.pageManager.refreshData()}
                            />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.fromDate} />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.toDate} />
                        </Neo.GridLayout>
                        <Neo.Button isSubmit variant="primary" icon="fa fa-search">Search</Neo.Button>
                        <Neo.Button variant="secondary"
                                    icon="fa fa-times"
                                    style={{ marginLeft: '10px' }}
                                    onClick={() => this.viewModel.clearSearch()}>
                            Clear
                        </Neo.Button>
                    </Neo.Form>
                </Neo.Card>

                {/* Trade Orders Grid Card */}
                <Neo.Card title={TextConstants.Titles.TradeOrders} icon="fa fa-list">
                        <Neo.Button
                            variant="success"
                            icon="fa fa-paper-plane"
                            style={{ marginRight: '10px' }}
                            onClick={this.viewModel.handleBulkRelease}
                            disabled={this.viewModel.selectedTradeOrderIDs.length === 0}
                        >
                            Release Selected
                        </Neo.Button>
                        <Neo.Button
                            variant="primary"
                            icon="fa fa-download"
                            style={{marginRight: '10px' }}
                            onClick={this.viewModel.handleBulkDownload}
                            tooltip="Download all filtered results as a .csv file"
                        >
                            Download
                        </Neo.Button>          
                    <Neo.Pager
                        pageManager={this.viewModel.pageManager}
                        pageControlProps={{ pageSizeLabel: "Show:" }}
                        style={{ paddingTop: "20px" }}
                    > 
                        <div className="row overflow-auto">
                            <NeoGrid.Grid<TradeOrderLookup>
                                items={this.viewModel.getFilteredItems()}
                                className="table-striped"
                                initialSort={'cycleDate'}
                                initialSortAscending={false}
                                allowSort
                            >
                                {(item: TradeOrderLookup) => {
                                    const isPending = item.tradeOrderStatusId === TradeOrderStatus.Pending;
                                    const isReleased = item.tradeOrderStatusId === TradeOrderStatus.Released;
                                    const isRejected = item.tradeOrderStatusId === TradeOrderStatus.Rejected;
                                    const canReject = [TradeOrderStatus.Pending, TradeOrderStatus.Released].includes(item.tradeOrderStatusId);

                                    return (
                                        <NeoGrid.Row key={item.tradeOrderId}>
                                            <NeoGrid.Column
                                                label={
                                                    <span>
                                                        <input
                                                            type="checkbox"
                                                            checked={this.viewModel.selectAll}
                                                            onChange={e => this.viewModel.handleSelectAll(e.target.checked)}
                                                            style={{ marginRight: 6 }}
                                                        />
                                                        Select All
                                                    </span>
                                                }
                                            >
                                                {isPending && (
                                                    <input
                                                        type="checkbox"
                                                        checked={this.viewModel.selectedTradeOrderIDs.includes(String(item.tradeOrderId))}
                                                        onChange={e => this.viewModel.handleSelect(item, e.target.checked)}
                                                    />
                                                )}
                                            </NeoGrid.Column>
                                            <NeoGrid.Column label="Status" display={item.meta.tradeOrderStatusName} />                                          
                                            <NeoGrid.Column label="Stockbroker Name" display={item.meta.staticServiceProviderFileGroupingName} />
                                            <NeoGrid.Column label="Trade Description" display={item.meta.tradeDescription} />
                                            <NeoGrid.Column label="Account Number" display={item.meta.accountNumber} />
                                            <NeoGrid.Column label="Fund Code" display={item.meta.fundCode} />
                                            <NeoGrid.Column label="Fund Name" display={item.meta.fundName} />
                                            <NeoGrid.Column label="Bulked Investor Code" display={item.meta.bulkedInvestorCode} />
                                            <NeoGrid.Column label="Bulked Investor Name" display={item.meta.bulkedInvestorName} />
                                            <NeoGrid.Column label="Product Code" display={item.meta.productCode} />
                                            <NeoGrid.Column label="Product Name" display={item.meta.productName} />                                
                                            <NeoGrid.Column label="Reference Number" display={item.meta.referenceNumber} />
                                            <NeoGrid.Column label="Percent" display={item.meta.percent} />
                                            <NeoGrid.Column label="Units" display={item.meta.units} />
                                            <NeoGrid.Column label="Currency" display={item.meta.currency} />
                                            <NeoGrid.Column label="Amount" display={item.meta.amount} />
                                            <NeoGrid.Column label="Confirmed Amount" display={item.meta.confirmedAmount ?? item.meta.amount ?? ""} />
                                            <NeoGrid.Column label="Cycle Date" display={item.meta.cycleDate} />
                                            <NeoGrid.Column label="Mode" display={item.meta.modeCode} />
                                            <NeoGrid.Column label="Fund Reference" display={item.meta.fundReference} />
                                            <NeoGrid.Column label="Message Type" display={item.meta.messageTypeName} />
                                            <NeoGrid.Column label="Service Provider Reference" display={item.meta.staticServiceProviderFileGroupingName} hidden/>
                                            <NeoGrid.Column label="Note" display={item.meta.note} />                                           
                                            <NeoGrid.ButtonColumn label="Actions">
                                                <div className="btn-group">
                                                    <Neo.Button
                                                        disabled={disableInlineActions || !isReleased}
                                                        variant="success"
                                                        size="sm"
                                                        isOutline
                                                        icon="fa fa-check"
                                                        title="Confirm"
                                                        onClick={() => this.viewModel.showConfirm(item)}
                                                    />
                                                    <Neo.Button
                                                        disabled={disableInlineActions || !canReject}
                                                        variant="danger"
                                                        size="sm"
                                                        isOutline
                                                        icon="fa fa-times"
                                                        title="Reject"
                                                        onClick={() => this.viewModel.showReject(item)}
                                                    />
                                                    <Neo.Button
                                                        disabled={disableInlineActions || !isRejected}
                                                        variant="warning"
                                                        size="sm"
                                                        isOutline
                                                        icon="fa fa-undo"
                                                        title="Re-Instate"
                                                        onClick={() => this.viewModel.showReinstate(item)}
                                                    />
                                                </div>
                                            </NeoGrid.ButtonColumn>
                                        </NeoGrid.Row>
                                    );
                                }}
                            </NeoGrid.Grid>
                        </div>
                    </Neo.Pager>                   
                </Neo.Card>

                {/* Confirm Modal */}
                <Neo.Modal
                    show={this.viewModel.showConfirmModal}
                    bindModel={this.viewModel.meta.confirmTradeOrder}
                    title="Confirm Trade Order"
                    onClose={this.viewModel.closeConfirm}
                    buttons={[
                        { text: "Save", variant: "success", icon: "fa fa-check", onClick: this.viewModel.submitConfirm },
                        { text: "Cancel", variant: "secondary", onClick: this.viewModel.closeConfirm }
                    ]}
                >
                    {(command: UpdateTradeOrderCommand) => (
                        <div>
                            {this.renderFileUpload('confirm-upload-file', 'confirm')}
                            <Neo.FormGroup 
                                bind={command.meta.amount} 
                                label="Original Amount"
                                isReadOnly={true}
                            />
                            <Neo.FormGroup 
                                bind={command.meta.confirmedAmount} 
                                label="Confirmed Amount"
                                placeholder="Enter confirmed amount"
                            />
                            <Neo.FormGroup 
                                bind={command.meta.note} 
                                label="Notes"
                                placeholder="eg. Reason for confirmation" 
                            />
                        </div>
                    )}
                </Neo.Modal>

                {/* Reject Modal */}
                <Neo.Modal
                    show={this.viewModel.showRejectModal}
                    bindModel={this.viewModel.meta.rejectTradeOrder}
                    title="Reject Trade Order"
                    onClose={this.viewModel.closeReject}
                    buttons={[
                        { text: "Reject", variant: "danger", icon: "fa fa-times", onClick: this.viewModel.submitReject },
                        { text: "Cancel", variant: "secondary", onClick: this.viewModel.closeReject }
                    ]}
                >
                    {(command: UpdateTradeOrderCommand) => (
                        <div>
                            {this.renderFileUpload('reject-upload-file', 'reject')}
                            <Neo.FormGroup 
                                bind={command.meta.note} 
                                label="Rejection Reason"
                                placeholder="Please provide reason for rejection" 
                            />
                        </div>
                    )}
                </Neo.Modal>

                {/* Reinstate Modal */}
                <Neo.Modal
                    show={this.viewModel.showReinstateModal}
                    bindModel={this.viewModel.meta.reinstateTradeOrder}
                    title="Re-instate Trade Order"
                    onClose={this.viewModel.closeReinstate}
                    buttons={[
                        { text: "Yes, Re-Instate", variant: "warning", icon: "fa fa-undo", onClick: this.viewModel.submitReinstate },
                        { text: "No, Cancel", variant: "secondary", onClick: this.viewModel.closeReinstate }
                    ]}
                >
                    {(command: UpdateTradeOrderCommand) => (
                        <div>
                            <p>Are you sure you want to re-instate this trade order?</p>
                            
                            <Neo.FormGroup 
                                bind={command.meta.note} 
                                label="Notes"
                                placeholder="Reason for reinstatement" 
                            />
                        </div>
                    )}
                </Neo.Modal>
            </>
        );
    }
}